import { useEffect, useRef, useCallback, useState } from 'react';
import type { ToolType } from '@/store/dungeonEditorStore';
import type { DungeonMapData, DungeonRoom } from '@/lib/db/dungeon-types';

// ─── Constants ────────────────────────────────────────────────────────────────

const CELL_SIZE = 16;
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.1;

const HOTKEY_MAP: Record<string, ToolType> = {
  v: 'select',
  h: 'hand',
  r: 'room',
  c: 'corridor',
  d: 'door',
  e: 'entity',
  t: 'trigger',
  x: 'erase',
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EditorInputCallbacks {
  setTool: (tool: ToolType) => void;
  setZoom: (zoom: number) => void;
  setPanOffset: (offset: { x: number; y: number }) => void;
  undo: () => void;
  redo: () => void;
  save: () => void;
  selectRoom: (id: string | null) => void;
  selectEntity: (id: string | null) => void;
  selectTrigger: (id: string | null) => void;
  clearSelection: () => void;
  removeRoom: (id: string) => void;
  removeEntity: (id: string) => void;
  removeTrigger: (id: string) => void;
  removeCorridor: (id: string) => void;
  onCellClick?: (x: number, y: number) => void;
  onCellDragStart?: (x: number, y: number) => void;
  onCellDragEnd?: (x: number, y: number) => void;
  onRoomClick?: (roomId: string) => void;
  onRoomDoubleClick?: (roomId: string) => void;
  onEntityClick?: (entityId: string) => void;
  onEntityDoubleClick?: (entityId: string) => void;
  onRoomDragStart?: (roomId: string, zone: RoomZone) => void;
  onRoomDrag?: (roomId: string, zone: RoomZone, dx: number, dy: number) => void;
  onRoomDragEnd?: (roomId: string) => void;
}

export interface EditorInputState {
  selectedTool: ToolType;
  zoom: number;
  panOffset: { x: number; y: number };
  mapData: DungeonMapData;
  selectedRoomId: string | null;
  selectedEntityId: string | null;
  selectedTriggerId: string | null;
}

export interface EditorInputResult {
  canvasHandlers: {
    onMouseDown: (e: React.MouseEvent<HTMLCanvasElement>) => void;
    onMouseMove: (e: React.MouseEvent<HTMLCanvasElement>) => void;
    onMouseUp: (e: React.MouseEvent<HTMLCanvasElement>) => void;
    onContextMenu: (e: React.MouseEvent) => void;
  };
  attachWheel: (canvas: HTMLCanvasElement | null) => void;
  cursor: string;
  hoverZone: RoomZone | null;
  dragPreview: { x: number; y: number; width: number; height: number } | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

function clampZoom(z: number): number {
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z));
}

function screenToGrid(
  screenX: number,
  screenY: number,
  canvas: HTMLCanvasElement,
  panOffset: { x: number; y: number },
  zoom: number,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const cx = screenX - rect.left;
  const cy = screenY - rect.top;
  return {
    x: Math.floor((cx - panOffset.x) / (CELL_SIZE * zoom)),
    y: Math.floor((cy - panOffset.y) / (CELL_SIZE * zoom)),
  };
}

function findRoomAt(gx: number, gy: number, data: DungeonMapData): DungeonRoom | undefined {
  return data.rooms.find(
    (r) => gx >= r.x && gx < r.x + r.width && gy >= r.y && gy < r.y + r.height,
  );
}

function findEntityAt(gx: number, gy: number, data: DungeonMapData) {
  return data.entities.find((e) => e.x === gx && e.y === gy);
}

export type RoomZone = 'move' | 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se';

function detectRoomZone(
  gx: number,
  gy: number,
  room: DungeonRoom,
): RoomZone | null {
  const { x, y, width, height } = room;
  if (gx < x || gx >= x + width || gy < y || gy >= y + height) return null;

  const atLeft = gx < x + 1;
  const atRight = gx >= x + width - 1;
  const atTop = gy < y + 1;
  const atBottom = gy >= y + height - 1;

  if (atTop && atLeft) return 'nw';
  if (atTop && atRight) return 'ne';
  if (atBottom && atLeft) return 'sw';
  if (atBottom && atRight) return 'se';
  if (atTop) return 'n';
  if (atBottom) return 's';
  if (atLeft) return 'w';
  if (atRight) return 'e';
  return 'move';
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useEditorInput(
  state: EditorInputState,
  callbacks: EditorInputCallbacks,
): EditorInputResult {
  // Stable refs so event handlers always see latest values without re-registering
  const stateRef = useRef(state);
  stateRef.current = state;

  const cbRef = useRef(callbacks);
  cbRef.current = callbacks;

  // Canvas element ref — set via attachWheel (the only place the actual DOM element is available)
  const canvasElRef = useRef<HTMLCanvasElement | null>(null);

  // Ephemeral interaction state refs
  const isSpaceHeldRef = useRef(false);
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const panOffsetAtStartRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const dragStartCellRef = useRef({ x: 0, y: 0 });
  const toolBeforeSpaceRef = useRef<ToolType | null>(null);

  // Double-click detection
  const lastClickRef = useRef<{ time: number; x: number; y: number } | null>(null);

  // Room drag state
  const roomDragRef = useRef<{
    roomId: string;
    zone: RoomZone;
    startGrid: { x: number; y: number };
    lastGrid: { x: number; y: number };
  } | null>(null);
  const [hoverZone, setHoverZone] = useState<RoomZone | null>(null);
  const hoverZoneRef = useRef<RoomZone | null>(null);
  const [dragCurrentCell, setDragCurrentCell] = useState<{ x: number; y: number } | null>(null);

  // Wheel cleanup
  const wheelCleanupRef = useRef<(() => void) | null>(null);

  // ── Fit / zoom helpers ─────────────────────────────────────────────────────

  const fitToContent = useCallback(() => {
    const canvas = canvasElRef.current;
    if (!canvas) return;
    const { mapData, zoom: currentZoom, panOffset } = stateRef.current;
    const rooms = mapData.rooms;

    if (rooms.length === 0) {
      // No rooms — center origin
      cbRef.current.setZoom(1);
      cbRef.current.setPanOffset({ x: canvas.width / 2, y: canvas.height / 2 });
      return;
    }

    const minX = Math.min(...rooms.map((r) => r.x));
    const minY = Math.min(...rooms.map((r) => r.y));
    const maxX = Math.max(...rooms.map((r) => r.x + r.width));
    const maxY = Math.max(...rooms.map((r) => r.y + r.height));

    const contentW = (maxX - minX) * CELL_SIZE;
    const contentH = (maxY - minY) * CELL_SIZE;
    const PAD = 40;

    const canvasW = canvas.clientWidth || canvas.width;
    const canvasH = canvas.clientHeight || canvas.height;

    const scaleX = (canvasW - PAD * 2) / contentW;
    const scaleY = (canvasH - PAD * 2) / contentH;
    const newZoom = clampZoom(Math.min(scaleX, scaleY));

    const newPanX = (canvasW - contentW * newZoom) / 2 - minX * CELL_SIZE * newZoom;
    const newPanY = (canvasH - contentH * newZoom) / 2 - minY * CELL_SIZE * newZoom;

    // suppress unused warning
    void currentZoom;
    void panOffset;

    cbRef.current.setZoom(newZoom);
    cbRef.current.setPanOffset({ x: newPanX, y: newPanY });
  }, []);

  const zoomTo100 = useCallback(() => {
    const canvas = canvasElRef.current;
    if (!canvas) return;
    const { zoom, panOffset } = stateRef.current;

    const canvasW = canvas.clientWidth || canvas.width;
    const canvasH = canvas.clientHeight || canvas.height;

    // Keep the center world point fixed
    const centerX = (canvasW / 2 - panOffset.x) / zoom;
    const centerY = (canvasH / 2 - panOffset.y) / zoom;

    const newPanX = canvasW / 2 - centerX * 1;
    const newPanY = canvasH / 2 - centerY * 1;

    cbRef.current.setZoom(1);
    cbRef.current.setPanOffset({ x: newPanX, y: newPanY });
  }, []);

  // ── Delete selected ────────────────────────────────────────────────────────

  const deleteSelected = useCallback(() => {
    const { selectedEntityId, selectedTriggerId, selectedRoomId, mapData } = stateRef.current;
    const cb = cbRef.current;

    if (selectedEntityId) {
      cb.removeEntity(selectedEntityId);
      cb.selectEntity(null);
      return;
    }
    if (selectedTriggerId) {
      cb.removeTrigger(selectedTriggerId);
      cb.selectTrigger(null);
      return;
    }
    if (selectedRoomId) {
      // Remove corridors connected to this room first
      const connectedCorridors = mapData.corridors.filter(
        (c) => c.fromRoomId === selectedRoomId || c.toRoomId === selectedRoomId,
      );
      connectedCorridors.forEach((c) => cb.removeCorridor(c.id));
      cb.removeRoom(selectedRoomId);
      cb.selectRoom(null);
    }
  }, []);

  // ── Keyboard handler ───────────────────────────────────────────────────────

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isInputFocused()) return;

      const ctrl = e.ctrlKey || e.metaKey;

      // Save
      if (ctrl && e.key === 's') {
        e.preventDefault();
        cbRef.current.save();
        return;
      }

      // Undo / Redo
      if (ctrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        cbRef.current.undo();
        return;
      }
      if (ctrl && (e.key === 'Z' || (e.key === 'z' && e.shiftKey) || e.key === 'y')) {
        e.preventDefault();
        cbRef.current.redo();
        return;
      }

      // Viewport shortcuts
      if (ctrl && e.key === '0') {
        e.preventDefault();
        fitToContent();
        return;
      }
      if (ctrl && e.key === '1') {
        e.preventDefault();
        zoomTo100();
        return;
      }
      if (ctrl && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        cbRef.current.setZoom(clampZoom(stateRef.current.zoom + ZOOM_STEP));
        return;
      }
      if (ctrl && e.key === '-') {
        e.preventDefault();
        cbRef.current.setZoom(clampZoom(stateRef.current.zoom - ZOOM_STEP));
        return;
      }

      // Space — temporary pan tool
      if (e.key === ' ' && !isSpaceHeldRef.current) {
        e.preventDefault();
        isSpaceHeldRef.current = true;
        toolBeforeSpaceRef.current = stateRef.current.selectedTool;
        cbRef.current.setTool('hand');
        return;
      }

      // Delete / Backspace
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        deleteSelected();
        return;
      }

      // Tool hotkeys (no modifiers)
      if (!ctrl && !e.altKey && !e.shiftKey) {
        const tool = HOTKEY_MAP[e.key.toLowerCase()];
        if (tool) {
          cbRef.current.setTool(tool);
        }
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        isSpaceHeldRef.current = false;
        if (toolBeforeSpaceRef.current !== null) {
          cbRef.current.setTool(toolBeforeSpaceRef.current);
          toolBeforeSpaceRef.current = null;
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [fitToContent, zoomTo100, deleteSelected]);

  // ── Mouse handlers ─────────────────────────────────────────────────────────

  const startPan = useCallback((clientX: number, clientY: number) => {
    isPanningRef.current = true;
    panStartRef.current = { x: clientX, y: clientY };
    panOffsetAtStartRef.current = { ...stateRef.current.panOffset };
  }, []);

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasElRef.current;
      if (!canvas) return;

      const s = stateRef.current;
      const cb = cbRef.current;

      const isPanTrigger =
        e.button === 1 || // middle click
        (e.button === 0 && e.altKey) || // alt + left
        (e.button === 0 && isSpaceHeldRef.current) || // space + left
        s.selectedTool === 'hand'; // hand tool

      if (isPanTrigger) {
        e.preventDefault();
        startPan(e.clientX, e.clientY);
        return;
      }

      if (e.button !== 0) return;

      const grid = screenToGrid(e.clientX, e.clientY, canvas, s.panOffset, s.zoom);

      // Double-click detection
      const now = Date.now();
      const last = lastClickRef.current;
      const isDoubleClick =
        last !== null &&
        now - last.time < 400 &&
        last.x === grid.x &&
        last.y === grid.y;

      lastClickRef.current = { time: now, x: grid.x, y: grid.y };

      if (isDoubleClick) {
        const entity = findEntityAt(grid.x, grid.y, s.mapData);
        if (entity && cb.onEntityDoubleClick) {
          cb.onEntityDoubleClick(entity.id);
          return;
        }
        const room = findRoomAt(grid.x, grid.y, s.mapData);
        if (room && cb.onRoomDoubleClick) {
          cb.onRoomDoubleClick(room.id);
          return;
        }
      }

      // Room move/resize: select tool + selected room + hover zone
      if (s.selectedTool === 'select' && s.selectedRoomId && !isDoubleClick) {
        const room = s.mapData.rooms.find((r) => r.id === s.selectedRoomId);
        if (room) {
          const zone = detectRoomZone(grid.x, grid.y, room);
          if (zone) {
            roomDragRef.current = {
              roomId: room.id,
              zone,
              startGrid: { x: grid.x, y: grid.y },
              lastGrid: { x: grid.x, y: grid.y },
            };
            cb.onRoomDragStart?.(room.id, zone);
            return;
          }
        }
      }

      // Item-selection tools (select/corridor) get entity/room click handling.
      // Other tools (entity, trigger, door, erase) need onCellClick for cell-level operations.
      const isItemSelectTool = s.selectedTool === 'select' || s.selectedTool === 'corridor';

      if (isItemSelectTool) {
        const entity = findEntityAt(grid.x, grid.y, s.mapData);
        if (entity) {
          if (cb.onEntityClick) cb.onEntityClick(entity.id);
          return;
        }

        const room = findRoomAt(grid.x, grid.y, s.mapData);
        if (room) {
          if (cb.onRoomClick) cb.onRoomClick(room.id);
          return;
        }
      }

      // Start drag (cell drag for drawing/placement tools)
      isDraggingRef.current = true;
      dragStartCellRef.current = grid;
      if (cb.onCellDragStart) cb.onCellDragStart(grid.x, grid.y);
    },
    [startPan],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (isPanningRef.current) {
        const dx = e.clientX - panStartRef.current.x;
        const dy = e.clientY - panStartRef.current.y;
        cbRef.current.setPanOffset({
          x: panOffsetAtStartRef.current.x + dx,
          y: panOffsetAtStartRef.current.y + dy,
        });
        return;
      }

      const canvas = canvasElRef.current;
      if (!canvas) return;
      const s = stateRef.current;
      const grid = screenToGrid(e.clientX, e.clientY, canvas, s.panOffset, s.zoom);

      if (roomDragRef.current) {
        const rd = roomDragRef.current;
        const dx = grid.x - rd.lastGrid.x;
        const dy = grid.y - rd.lastGrid.y;
        if (dx !== 0 || dy !== 0) {
          rd.lastGrid = { x: grid.x, y: grid.y };
          cbRef.current.onRoomDrag?.(rd.roomId, rd.zone, dx, dy);
        }
        return;
      }

      // Cell drag tracking (for room preview)
      if (isDraggingRef.current) {
        setDragCurrentCell({ x: grid.x, y: grid.y });
        return;
      }

      // Update hover zone (only triggers re-render when zone changes)
      let newZone: RoomZone | null = null;
      if (s.selectedTool === 'select' && s.selectedRoomId) {
        const room = s.mapData.rooms.find((r) => r.id === s.selectedRoomId);
        if (room) {
          newZone = detectRoomZone(grid.x, grid.y, room);
        }
      }
      if (newZone !== hoverZoneRef.current) {
        hoverZoneRef.current = newZone;
        setHoverZone(newZone);
      }
    },
    [],
  );

  const onMouseUp = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (isPanningRef.current) {
        isPanningRef.current = false;
        return;
      }

      if (roomDragRef.current) {
        const rd = roomDragRef.current;
        roomDragRef.current = null;
        hoverZoneRef.current = null;
        setHoverZone(null);
        cbRef.current.onRoomDragEnd?.(rd.roomId);
        return;
      }

      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        setDragCurrentCell(null);
        const canvas = canvasElRef.current;
        if (!canvas) return;
        const s = stateRef.current;
        const cb = cbRef.current;
        const grid = screenToGrid(e.clientX, e.clientY, canvas, s.panOffset, s.zoom);
        const start = dragStartCellRef.current;

        if (grid.x === start.x && grid.y === start.y) {
          if (cb.onCellClick) cb.onCellClick(grid.x, grid.y);
        } else {
          if (cb.onCellDragEnd) cb.onCellDragEnd(grid.x, grid.y);
        }
      }
    },
    [],
  );

  const onContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  // ── Wheel (zoom-to-cursor) ─────────────────────────────────────────────────

  const attachWheel = useCallback((canvas: HTMLCanvasElement | null) => {
    canvasElRef.current = canvas;
    if (wheelCleanupRef.current) {
      wheelCleanupRef.current();
      wheelCleanupRef.current = null;
    }
    if (!canvas) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const s = stateRef.current;
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const worldX = (mouseX - s.panOffset.x) / s.zoom;
      const worldY = (mouseY - s.panOffset.y) / s.zoom;
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      const newZoom = clampZoom(s.zoom + delta);
      const newPanX = mouseX - worldX * newZoom;
      const newPanY = mouseY - worldY * newZoom;
      cbRef.current.setZoom(newZoom);
      cbRef.current.setPanOffset({ x: newPanX, y: newPanY });
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    wheelCleanupRef.current = () => canvas.removeEventListener('wheel', onWheel);
  }, []);

  // ── Cursor ─────────────────────────────────────────────────────────────────

  const getCursor = (): string => {
    if (isPanningRef.current) return 'grabbing';
    if (isSpaceHeldRef.current) return 'grab';

    if (roomDragRef.current) {
      const zone = roomDragRef.current.zone;
      if (zone === 'move') return 'move';
      if (zone === 'nw' || zone === 'se') return 'nwse-resize';
      if (zone === 'ne' || zone === 'sw') return 'nesw-resize';
      if (zone === 'n' || zone === 's') return 'ns-resize';
      if (zone === 'w' || zone === 'e') return 'ew-resize';
    }

    if (state.selectedTool === 'select' && hoverZone) {
      const zone = hoverZone;
      if (zone === 'move') return 'move';
      if (zone === 'nw' || zone === 'se') return 'nwse-resize';
      if (zone === 'ne' || zone === 'sw') return 'nesw-resize';
      if (zone === 'n' || zone === 's') return 'ns-resize';
      if (zone === 'w' || zone === 'e') return 'ew-resize';
    }

    switch (state.selectedTool) {
      case 'hand': return 'grab';
      case 'select': return 'default';
      case 'door': return 'pointer';
      default: return 'crosshair';
    }
  };

  // Compute drag preview rect (for room tool)
  let dragPreview: EditorInputResult['dragPreview'] = null;
  if (dragCurrentCell && state.selectedTool === 'room') {
    const start = dragStartCellRef.current;
    const rx = Math.min(start.x, dragCurrentCell.x);
    const ry = Math.min(start.y, dragCurrentCell.y);
    const rw = Math.abs(dragCurrentCell.x - start.x) + 1;
    const rh = Math.abs(dragCurrentCell.y - start.y) + 1;
    dragPreview = { x: rx, y: ry, width: rw, height: rh };
  }

  return {
    canvasHandlers: {
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onContextMenu,
    },
    attachWheel,
    cursor: getCursor(),
    hoverZone,
    dragPreview,
  };
}
